import { useQuery } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { Language } from "@shared/types/user";

type UseLanguagesResult = {
  languages: Language[];
  isLoading: boolean;
  error: Error | null;
};

/**
 * Hook to fetch and cache languages data.
 * Languages are static metadata, cached indefinitely.
 * 
 * Note: This is in-memory cache (per session). Data is cleared on page refresh.
 */
export default function useLanguages(): UseLanguagesResult {
  const { GET } = useApiRequest();
  const {
    data: languages = [],
    isLoading,
    error,
  } = useQuery<Language[]>({
    queryKey: ["languages"],
    queryFn: () => GET("/api/metadata/languages"),
    // Languages are static metadata - cache indefinitely (in-memory)
    staleTime: Infinity,
    // Keep in memory for 30 days even when not actively used (garbage collection)
    gcTime: 30 * 24 * 60 * 60 * 1000, // 30 days
  });

  // Sort languages by englishLabel alphabetically
  const sortedLanguages = [...languages].sort((a, b) =>
    a.englishLabel.localeCompare(b.englishLabel)
  );

  return {
    languages: sortedLanguages,
    isLoading,
    error: error ?? null,
  } satisfies UseLanguagesResult;
}
